/**
 * HowMuchToEatSection - Wrapper component for the "How much to eat" section in MealDetails
 *
 * This component:
 * 1. Wraps the legacy HowMuchToEat component with new context integration
 * 2. Handles the section styling and click behavior to open editMealBatches
 * 3. Calculates proper scaling factors for leftovers
 * 4. Manages multi-dish display with proper dish numbering
 */

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import HowMuchToEat from '@components/Recipes/HowMuchToEat.react';
import FoodUnitsSelector from '@components/DailyLog/FoodUnitsSelectorModal.react';
import { useMealPlanData, useModalControls, useAssets, useMealOperations } from '@components/Plans/Editor/contexts';
import { getPrimaryMeal, getParticipantsForSpecificMeal } from '@utils/Meals';
import { calculateTotalServingsForParticipants, getBaseServings } from '@utils/servingsCalculations';

const HowMuchToEatSection = ({ 
    meals, 
    participants, 
    profile,
    recipes,
    sectionClass,
    onChangePortionAmount
}) => {
    const { plan } = useMealPlanData();
    const { editMealBatches } = useModalControls();
    const { foods } = useAssets();
    const { updateMeal } = useMealOperations();

    // Calculate parent meal and leftover relationship
    const primaryMeal = meals[0];
    const parent = useMemo(() => {
        if (primaryMeal?.meal_type === 'leftover' && primaryMeal?.parent) {
            return plan?.items?.find(meal => 
                meal.id === primaryMeal.parent && !meal.deleted
            );
        }
        return null;
    }, [primaryMeal, plan]);

    const hasChild = useMemo(() => {
        if (!primaryMeal?.id) return false;
        return plan?.items?.some(meal => 
            meal.parent === primaryMeal.id && 
            meal.meal_type === 'leftover' && 
            !meal.deleted
        );
    }, [primaryMeal, plan]);

    const handleFoodChangeAmount = useCallback(async (meal, logged_portion, logged_unit, logged_amount, logged_grams, logged_milliliters) => {
        await updateMeal(meal.id || meal.uuid, { logged_portion, logged_unit, logged_amount, logged_grams, logged_milliliters });
    }, [updateMeal]);

    // Calculate surplus state (last day of leftovers)
    const surplus = useMemo(() => {
        if (!parent) {
            // For non-leftover meals, surplus is true if there are no child leftovers
            return !hasChild;
        }

        const daysPassed = primaryMeal.offset - parent.offset;
        const totalDays = plan?.items?.filter(meal =>
            meal.parent === parent.id &&
            meal.meal_type === 'leftover' &&
            !meal.deleted
        ).length + 1; // +1 for original meal

        return totalDays - daysPassed === 1;
    }, [parent, primaryMeal, plan, hasChild]);

    // Calculate total servings allocated to leftover meals
    const leftoverServingsPerDish = useMemo(() => {
        if (!primaryMeal?.id || !plan?.items) {
            return meals.map(() => 0);
        }

        // For leftover meals: sum servings of FUTURE sibling leftovers only
        if (parent) {
            const futureSiblings = plan.items.filter(m =>
                m.parent === primaryMeal.parent &&
                m.meal_type === 'leftover' &&
                m.offset > primaryMeal.offset &&
                !m.deleted
            );

            return meals.map(meal => {
                const dish = getPrimaryMeal([meal], recipes);
                if (!(dish && dish.recipe)) return 0;
                let total = meal.extra_servings || 0;
                futureSiblings.forEach(siblingMeal => {
                    const participants = getParticipantsForSpecificMeal(siblingMeal, profile);
                    total += calculateTotalServingsForParticipants(participants, siblingMeal, dish.recipe);
                });
                return total;
            });
        }

        // For parent meals: sum servings across all child leftover meals
        const childLeftovers = plan.items.filter(meal =>
            meal.parent === primaryMeal.id &&
            meal.meal_type === 'leftover' &&
            !meal.deleted
        );

        if (childLeftovers.length === 0) {
            return meals.map(meal => meal.extra_servings || 0);
        }

        return meals.map(meal => {
            const dish = getPrimaryMeal([meal], recipes);
            if (!(dish && dish.recipe)) {
                return 0;
            }

            let totalLeftoverServings = meal.extra_servings || 0;

            childLeftovers.forEach(leftoverMeal => {
                const leftoverParticipants = getParticipantsForSpecificMeal(leftoverMeal, profile);
                totalLeftoverServings += calculateTotalServingsForParticipants(
                    leftoverParticipants,
                    leftoverMeal,
                    dish.recipe
                );
            });

            return totalLeftoverServings;
        });
    }, [primaryMeal, plan, parent, meals, recipes, profile]);

    // Create tabs for each dish
    const eatThisMuchTabs = useMemo(() => {
        return meals
            .map((meal, i) => {
                const dish = getPrimaryMeal([meal], recipes, foods);

                if (!(dish && (dish.recipe || dish.food))) {
                    return null;
                }

                // Food items: chart hidden (preserves layout), show only main participant with their logged amount
                if (dish.food && !dish.recipe) {
                    const food = dish.food;
                    const mealForDisplay = { logged_amount: 1, logged_unit: 'serving', ...meal };
                    const displayAmount = mealForDisplay.logged_amount;
                    const displayUnit = mealForDisplay.logged_unit;
                    return (
                        <section key={i} data-title={`Dish ${i + 1}`}>
                            <section className="how-much-to-eat how-much-to-eat--food">
                                <ul className="portion-key">
                                    <li>portion sizes for:</li>
                                    <li>
                                        {meals.length > 1 ? <span className="dish-number">{`DISH ${i + 1}: `}</span> : null}
                                        <em>{food.name || food.title}</em>
                                    </li>
                                    <li className="participant-item">
                                        <div className="key-color" style={{ backgroundColor: '#be3b8b' }}></div>
                                        <div className="participant-info">
                                            <span className="member-name">{profile.first_name || profile.name}</span>
                                            <span className="slice-info">
                                                {' • '}
                                                {displayAmount}{' '}{displayUnit}
                                                <span onClick={(e) => e.stopPropagation()}>
                                                    <FoodUnitsSelector
                                                        food={food}
                                                        meal={mealForDisplay}
                                                        profile={profile}
                                                        onChangeAmount={(logged_portion, logged_unit, logged_amount, logged_grams, logged_milliliters) => {
                                                            handleFoodChangeAmount(meal, logged_portion, logged_unit, logged_amount, logged_grams, logged_milliliters);
                                                        }}
                                                        buttonText="change amount"
                                                        howMuchQuestion="How much to eat:"
                                                    />
                                                </span>
                                            </span>
                                        </div>
                                    </li>
                                </ul>
                            </section>
                        </section>
                    );
                }

                // Derive parent for THIS dish specifically (not the slot-level primaryMeal parent)
                const dishParent = meal.meal_type === 'leftover' && meal.parent
                    ? plan?.items?.find(m => m.id === meal.parent && !m.deleted)
                    : null;

                let factor = 1;
                let scaling = meal.scaling;

                // Handle leftover calculations
                if (dishParent) {
                    scaling = dishParent.scaling;
                    const daysPassed = meal.offset - dishParent.offset;

                    if (daysPassed >= 1) {
                        const recipe = dish.recipe;
                        const baseServings = getBaseServings(recipe);
                        const totalYield = scaling * baseServings;

                        // Calculate actual servings consumed on previous days
                        // We need to look at all leftover meals between parent and current meal
                        let servingsConsumedOnPreviousDays = 0;

                        // Get all leftover meals from this parent, sorted by offset
                        const allLeftovers = plan?.items?.filter(m =>
                            m.parent === dishParent.id &&
                            m.meal_type === 'leftover' &&
                            m.offset < meal.offset &&
                            !m.deleted
                        ) || [];

                        // Add servings consumed on original parent meal
                        const parentParticipants = getParticipantsForSpecificMeal(dishParent, profile);
                        servingsConsumedOnPreviousDays += calculateTotalServingsForParticipants(
                            parentParticipants,
                            dishParent,
                            recipe
                        );

                        // Add servings consumed on each previous leftover meal
                        allLeftovers.forEach(leftoverMeal => {
                            const leftoverParticipants = getParticipantsForSpecificMeal(leftoverMeal, profile);
                            servingsConsumedOnPreviousDays += calculateTotalServingsForParticipants(
                                leftoverParticipants,
                                leftoverMeal,
                                recipe
                            );
                        });

                        const remainingServings = totalYield - servingsConsumedOnPreviousDays;
                        factor = remainingServings > 0 ? remainingServings / totalYield : 0;
                    }
                }

                const parentMealDay = dishParent
                    ? `Day ${dishParent.offset + 1}`
                    : null;

                return (
                    <section key={i} data-title={`Dish ${i + 1}`}>
                        <HowMuchToEat
                            dishNumber={meals.length > 1 ? i + 1 : 0}
                            uuid={meal.uuid || meal.id}
                            meal={meal}
                            profile={profile}
                            recipe={dish.recipe}
                            participants={participants}
                            factor={factor}
                            scaling={scaling}
                            surplus={surplus}
                            leftoverServings={leftoverServingsPerDish[i] || 0}
                            onClick={() => editMealBatches && editMealBatches(meal)}
                            isLeftover={!!dishParent}
                            parentMealDay={parentMealDay}
                            onChangePortionAmount={onChangePortionAmount}
                        />
                    </section>
                );
            })
            .filter(Boolean);
    }, [meals, recipes, foods, plan, parent, participants, profile, surplus, leftoverServingsPerDish, editMealBatches, onChangePortionAmount, handleFoodChangeAmount]);

    const handleSectionClick = useCallback(() => {
        if (primaryMeal && editMealBatches) {
            editMealBatches(primaryMeal);
        }
    }, [editMealBatches, primaryMeal]);

    if (eatThisMuchTabs.length === 0) {
        return null;
    }

    return (
        <div 
            className={classnames('details-section', sectionClass)}
            onClick={handleSectionClick}
            style={{ cursor: 'pointer' }}
        >
            <header>
                <div className="section-title">
                    {primaryMeal?.meal_type === 'leftover' && parent ? (
                        <div className="leftover-badge">
                            Leftover from Day {parseInt(parent.offset) + 1}
                        </div>
                    ) : null}
                    <p className="t2">
                        How much to eat
                    </p>
                </div>
            </header>
            
            <div className="section-body">
                {eatThisMuchTabs}
            </div>
        </div>
    );
};

HowMuchToEatSection.propTypes = {
    meals: PropTypes.array.isRequired,
    participants: PropTypes.array.isRequired,
    profile: PropTypes.object.isRequired,
    recipes: PropTypes.object.isRequired,
    sectionClass: PropTypes.string,
    onChangePortionAmount: PropTypes.func,
};

export default HowMuchToEatSection;